import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { withStateLoading } from '@/util/dataState';
import {
    type SurgicalGuideOrder,
    type SurgicalGuideOrdersState,
    SurgicalGuideOrderState,
} from '@/stores/surgicalGuideOrders/types';
import {
    type ApiSurgicalGuideOrder,
    getSurgicalGuideModels,
    getSurgicalGuideOrders,
    getZippedDicoms,
    makeSurgicalGuideOrder,
} from '@/stores/surgicalGuideOrders/api';
import { makeInitialState } from '@/stores/surgicalGuideOrders/initialState';
import { mapToStateOrder } from '@/stores/surgicalGuideOrders/mapToState';

export const useSurgicalGuideOrders = defineStore('surgical-guide-orders', () => {
    const _state = ref<SurgicalGuideOrdersState>(makeInitialState());
    const newOrderPlanId = ref<number | null>(null);

    const hasOrders = computed(() => _state.value.orders.length > 0);

    async function load(caseId: number): Promise<void> {
        $reset();
        await withStateLoading(_state.value, async () => {
            const orders = await getSurgicalGuideOrders(caseId);
            orders.items.forEach((apiOrder: ApiSurgicalGuideOrder) => {
                const order: SurgicalGuideOrder = mapToStateOrder(apiOrder);
                _state.value.orders.push(order);
            });
        });
    }

    async function placeOrder(caseId: number, planId: number): Promise<void> {
        const apiOrder = await makeSurgicalGuideOrder(caseId, planId);
        const order = mapToStateOrder(apiOrder);
        _state.value.orders.push(order);
    }

    function getPlanOrder(planId: number): SurgicalGuideOrder | null {
        let order = null;
        _state.value.orders.forEach((_order: SurgicalGuideOrder) => {
            if (_order.planId === planId) {
                order = _order;
            }
        });
        return order;
    }

    function getLatestOrder(): SurgicalGuideOrder | null {
        let order = null;
        _state.value.orders.forEach((_order: SurgicalGuideOrder) => {
            if (_order.state === SurgicalGuideOrderState.Ordered) {
                order = _order;
            }
        });
        return order;
    }

    async function downloadScan(caseId: number, orderId: number) {

        try {
            await getZippedDicoms(caseId, orderId);
        } catch (error) {
            console.error('Failed to download ct scan zip', error);
            throw error;
        }
    }

    async function downloadModels(caseId: number, orderId: number) {

        try {
            await getSurgicalGuideModels(caseId, orderId);
        } catch (error) {
            console.error('Failed to download surgical guide zip', error);
            throw error;
        }
    }

    function $reset(): void {
        _state.value = makeInitialState();
        newOrderPlanId.value = null;
    }

    return {
        orders: computed(() => _state.value.orders),
        hasOrders,
        newOrderPlanId,
        load,
        placeOrder,
        getPlanOrder,
        getLatestOrder,
        downloadScan,
        downloadModels
    };
});
